export type ProgressStream<TData, TError> =
  | { type: 'progress'; progress: number }
  | { type: 'success'; success: TData }
  | { type: 'error'; error: TError };

export class ProgressStreamError<
  TError extends { message: string },
> extends Error {
  constructor(public readonly error: TError) {
    super('The progress stream reported an error.');
    this.name = 'ProgressStreamError';
  }
}

export class ProgressStreamParseError extends Error {
  constructor(cause: unknown) {
    super('The progress stream contained invalid JSON.', { cause });
    this.name = 'ProgressStreamParseError';
  }
}

export function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    error.name === 'AbortError'
  );
}

export async function* readProgressStream<TData, TError>(
  response: Response,
): AsyncGenerator<ProgressStream<TData, TError>> {
  if (!response.body) {
    throw new Error('Streaming responses are unavailable.');
  }

  const stream = response.body.pipeThrough(new TextDecoderStream());
  let buffer = '';

  const parseLine = (line: string) => {
    line = line.trim();
    if (!line) {
      return undefined;
    }

    let event: ProgressStream<TData, TError>;
    try {
      event = JSON.parse(line) as ProgressStream<TData, TError>;
    } catch (error) {
      throw new ProgressStreamParseError(error);
    }

    switch (event.type) {
      case 'progress':
      case 'success':
      case 'error':
        return event;
      default:
        throw new Error('The progress stream contained an unknown event.');
    }
  };

  for await (const chunk of stream) {
    buffer += chunk;
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      const event = parseLine(line);
      if (event) {
        yield event;
      }
    }
  }

  const finalEvent = parseLine(buffer);
  if (finalEvent) {
    yield finalEvent;
  }
}
